import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { mfgBomsApi, mfgBomKeys, useMfgBom, type AddMfgBomBody, type MfgPricingResult, type MfgUnitDimension } from "@/lib/mfg-boms";
import type { MfgItemRow } from "@/lib/mfg-catalog";
import { useMfgTree, collectGroups } from "./useMfgTree";
import { MfgRootPicker } from "./MfgRootPicker";
import { MfgSelectionsForm } from "./MfgSelectionsForm";
import { MfgDimensionsForm } from "./MfgDimensionsForm";
import { MfgPriceFooter } from "./MfgPriceFooter";

export interface MfgBuilderContext { kind: "quote" | "job"; id: number; }

/** Map a flat list of selected child ids back into the per-group `{ groupId: childId }` shape. */
function mapSelectionsToGroups(selected: number[], groups: { node: { item: { mfgItemId: number } }; options: { item: { mfgItemId: number } }[] }[]): Record<number, number> {
  const rec: Record<number, number> = {};
  for (const g of groups) for (const o of g.options) if (selected.includes(o.item.mfgItemId)) rec[g.node.item.mfgItemId] = o.item.mfgItemId;
  return rec;
}

export function MfgBuilderPanel({ context, mfgBomId, onAdded, onCancel }: { context: MfgBuilderContext; mfgBomId?: number; onAdded: () => void; onCancel: () => void }) {
  const qc = useQueryClient();
  const isEdit = mfgBomId != null;
  const { data: mfgBom } = useMfgBom(mfgBomId ?? 0);
  const [root, setRoot] = useState<MfgItemRow | null>(null);
  const { data: tree } = useMfgTree(root?.mfgItemId ?? 0);
  const groups = useMemo(() => (tree ? collectGroups(tree) : { variants: [], alternatives: [], materials: [] }), [tree]);

  const [qty, setQty] = useState(1);
  const [markup, setMarkup] = useState(0);
  const [variantSel, setVariantSel] = useState<Record<number, number>>({});
  const [altSel, setAltSel] = useState<Record<number, number>>({});
  const [dims, setDims] = useState<Record<number, MfgUnitDimension>>({});
  const [price, setPrice] = useState<MfgPricingResult | null>(null);
  const [pricing, setPricing] = useState(false);

  // Hydration guards — seed once (scalars from the BOM, selections once the tree resolves),
  // then leave the user's subsequent edits alone. Only ever runs in edit mode.
  const seededScalars = useRef(false);
  const seededSelections = useRef(false);

  // Seed root/qty/markup/dims from the loaded BOM (kicks off the tree walk via root).
  useEffect(() => {
    if (!isEdit || !mfgBom || seededScalars.current) return;
    seededScalars.current = true;
    setRoot({ mfgItemId: mfgBom.rootMfgItemId, name: mfgBom.displayProductName ?? "", itemTypeCode: "Assembly", itemNumber: null, description: null, unitOfMeasureCode: null, deleted: false, rowVersionBase64: null });
    setQty(mfgBom.orderQty);
    setMarkup(mfgBom.markupPct);
    setDims(Object.fromEntries(mfgBom.unitDimensions.map((d) => [d.materialMfgItemId, d])));
  }, [isEdit, mfgBom]);

  // Seed variant/alternative selections once the walked groups are available.
  useEffect(() => {
    if (!isEdit || !mfgBom || !tree || seededSelections.current) return;
    seededSelections.current = true;
    setVariantSel(mapSelectionsToGroups(mfgBom.variantSelections, groups.variants));
    setAltSel(mapSelectionsToGroups(mfgBom.alternativeSelections, groups.alternatives));
  }, [isEdit, mfgBom, tree, groups]);

  const body = useMemo<AddMfgBomBody | null>(() => {
    if (!root) return null;
    return {
      rootMfgItemId: root.mfgItemId, orderQty: qty, forDate: null,
      variantSelections: Object.values(variantSel), alternativeSelections: Object.values(altSel),
      unitDimensions: Object.values(dims), markupPct: markup, adjustAmt: 0, description: root.name,
    };
  }, [root, qty, markup, variantSel, altSel, dims]);

  // Debounced live preview whenever inputs change.
  useEffect(() => {
    if (!body) { setPrice(null); return; }
    setPricing(true);
    const h = setTimeout(() => {
      mfgBomsApi.preview(body).then((r) => setPrice(r)).catch(() => setPrice(null)).finally(() => setPricing(false));
    }, 300);
    return () => clearTimeout(h);
  }, [body]);

  const invalidate = () => {
    const pt = context.kind === "quote" ? "Quote" : "Job";
    void qc.invalidateQueries({ queryKey: ["boms", pt, context.id] });
    if (context.kind === "quote") {
      void qc.invalidateQueries({ queryKey: ["quote", context.id] });
      void qc.invalidateQueries({ queryKey: ["quote-items", context.id] });
    } else {
      void qc.invalidateQueries({ queryKey: ["job", context.id] });
      void qc.invalidateQueries({ queryKey: ["jobs", context.id, "items"] });
    }
    if (isEdit) void qc.invalidateQueries({ queryKey: mfgBomKeys.detail(mfgBomId) });
  };

  const attach = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        await mfgBomsApi.patch(mfgBomId, {
          orderQty: qty, markupPct: markup,
          variantSelections: Object.values(variantSel), alternativeSelections: Object.values(altSel),
          unitDimensions: Object.values(dims),
        }, mfgBom?.rowVersionBase64 ?? null);
        return;
      }
      await (context.kind === "quote" ? mfgBomsApi.addToQuote(context.id, body!) : mfgBomsApi.addToJob(context.id, body!));
    },
    onSuccess: () => {
      invalidate();
      toast.success(isEdit ? "Changes saved" : "Added to " + context.kind);
      onAdded();
    },
    onError: (e) => toast.error(e instanceof ApiError && e.status === 409 ? `This ${context.kind} is locked — items are read-only.` : `${isEdit ? "Save" : "Add"} failed: ${(e).message}`),
  });

  // In edit mode wait for the BOM before showing the picker (root seeds from it).
  if (!root) return isEdit ? <div className="py-8 text-center text-sm text-muted-foreground">Loading build…</div> : <MfgRootPicker onPick={setRoot} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="font-semibold">{root.name}</div>
        {!isEdit && <Button variant="ghost" size="sm" onClick={() => setRoot(null)}>Change product</Button>}
      </div>
      <MfgSelectionsForm
        variants={groups.variants} alternatives={groups.alternatives}
        variantSel={variantSel} altSel={altSel}
        onVariant={(g, c) => setVariantSel((s) => ({ ...s, [g]: c }))}
        onAlt={(g, c) => setAltSel((s) => ({ ...s, [g]: c }))}
      />
      <MfgDimensionsForm materials={groups.materials} dims={dims} onChange={(id, d) => setDims((s) => ({ ...s, [id]: d }))} />
      <div className="flex gap-3">
        <div><Label>Qty</Label><Input type="number" value={qty} onChange={(e) => setQty(Number(e.target.value))} className="w-24" /></div>
        <div><Label>Markup %</Label><Input type="number" value={markup} onChange={(e) => setMarkup(Number(e.target.value))} className="w-24" /></div>
      </div>
      <MfgPriceFooter result={price} pending={pricing} />
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => attach.mutate()} disabled={attach.isPending || !body}>{isEdit ? "Save changes" : `Add to ${context.kind}`}</Button>
      </div>
    </div>
  );
}
